// 全局缓存时长配置 (毫秒)
const DEFAULT_CACHE_DURATION = 60 * 60 * 1000; // 5分钟

// 缓存存储键名
const CACHE_STORAGE_KEY = 'web3_cache_data';

// BSC RPC 端点
const BSC_RPC_URL = 'https://bsc-dataseed3.defibit.io';

// BigInt 标记前缀
const BIGINT_MARKER = '__BIGINT__:';

// 内存缓存对象
let memoryCache = {};

// Web3 实例
let web3Instance = null;

/**
 * 获取Web3实例
 */
function getWeb3() {
    if (!web3Instance) {
        const { Web3 } = require('web3');
        web3Instance = new Web3(BSC_RPC_URL);
    }
    return web3Instance;
}

/**
 * 序列化BigInt - 将对象中的BigInt转换为带标记的字符串
 */
function serializeBigInt(obj) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    
    if (typeof obj === 'bigint') {
        return BIGINT_MARKER + obj.toString();
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => serializeBigInt(item));
    }
    
    if (typeof obj === 'object') {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
            result[key] = serializeBigInt(value);
        }
        return result;
    }
    
    return obj;
}

/**
 * 反序列化BigInt - 将对象中带标记的字符串转换回BigInt
 */
function deserializeBigInt(obj) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    
    if (typeof obj === 'string' && obj.startsWith(BIGINT_MARKER)) {
        return BigInt(obj.slice(BIGINT_MARKER.length));
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => deserializeBigInt(item));
    }
    
    if (typeof obj === 'object') {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
            result[key] = deserializeBigInt(value);
        }
        return result;
    }
    
    return obj;
}

/**
 * 缓存工具类
 */
class CachedWeb3Utils {
    constructor() {
        this.loadCacheFromStorage();
    }

    /**
     * 从localStorage加载缓存数据
     */
    loadCacheFromStorage() {
        try {
            const cachedData = localStorage.getItem(CACHE_STORAGE_KEY);
            if (cachedData) {
                const parsedData = JSON.parse(cachedData);
                // 反序列化BigInt
                memoryCache = {};
                for (const [key, value] of Object.entries(parsedData)) {
                    memoryCache[key] = {
                        data: deserializeBigInt(value.data),
                        timestamp: value.timestamp
                    };
                }
            }
        } catch (error) {
            console.warn('Failed to load cache from localStorage:', error);
            memoryCache = {};
        }
    }

    /**
     * 将缓存数据同步到localStorage
     */
    saveCacheToStorage() {
        try {
            // 序列化BigInt
            const serializedCache = {};
            for (const [key, value] of Object.entries(memoryCache)) {
                serializedCache[key] = {
                    data: serializeBigInt(value.data),
                    timestamp: value.timestamp
                };
            }
            localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(serializedCache));
        } catch (error) {
            console.warn('Failed to save cache to localStorage:', error);
        }
    }

    /**
     * 生成缓存键
     */
    generateCacheKey(functionName, ...params) {
        return `${functionName}_${params.join('_')}`;
    }

    /**
     * 检查缓存是否有效
     */
    isCacheValid(cacheKey, customDuration = DEFAULT_CACHE_DURATION) {
        const cacheData = memoryCache[cacheKey];
        if (!cacheData) return false;
        
        const now = Date.now();
        return (now - cacheData.timestamp) < customDuration;
    }

    /**
     * 获取缓存数据
     */
    getCacheData(cacheKey) {
        return memoryCache[cacheKey]?.data;
    }

    /**
     * 设置缓存数据
     */
    setCacheData(cacheKey, data) {
        memoryCache[cacheKey] = {
            data: data,
            timestamp: Date.now()
        };
        this.saveCacheToStorage();
    }

    /**
     * 通用缓存执行器
     */
    async executeWithCache(functionName, asyncFunction, forceUpdate = false, customDuration = DEFAULT_CACHE_DURATION, ...params) {
        const cacheKey = this.generateCacheKey(functionName, ...params);
        
        // 如果不强制更新且缓存有效，返回缓存数据
        if (!forceUpdate && this.isCacheValid(cacheKey, customDuration)) {
            return this.getCacheData(cacheKey);
        }

        // 执行异步函数获取新数据
        try {
            const result = await asyncFunction();
            this.setCacheData(cacheKey, result);
            return result;
        } catch (error) {
            // 如果请求失败且有缓存数据，返回缓存数据
            if (this.getCacheData(cacheKey) !== undefined) {
                console.warn(`Request failed, returning cached data for ${functionName}:`, error);
                return this.getCacheData(cacheKey);
            }
            throw error;
        }
    }

    /**
     * 获取缓存的Pool Token0地址
     */
    async getCachedPoolToken0(poolAddress, forceUpdate = false) {
        return this.executeWithCache(
            'poolToken0',
            async () => {
                const web3 = getWeb3();
                const poolContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "token0",
                        "outputs": [{"internalType": "address", "name": "", "type": "address"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], poolAddress);
                return await poolContract.methods.token0().call();
            },
            forceUpdate,
            DEFAULT_CACHE_DURATION,
            poolAddress
        );
    }

    /**
     * 获取缓存的Pool Token1地址
     */
    async getCachedPoolToken1(poolAddress, forceUpdate = false) {
        return this.executeWithCache(
            'poolToken1',
            async () => {
                const web3 = getWeb3();
                const poolContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "token1",
                        "outputs": [{"internalType": "address", "name": "", "type": "address"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], poolAddress);
                return await poolContract.methods.token1().call();
            },
            forceUpdate,
            DEFAULT_CACHE_DURATION,
            poolAddress
        );
    }

    /**
     * 获取缓存的Pool Fee
     */
    async getCachedPoolFee(poolAddress, forceUpdate = false) {
        return this.executeWithCache(
            'poolFee',
            async () => {
                const web3 = getWeb3();
                const poolContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "fee",
                        "outputs": [{"internalType": "uint24", "name": "", "type": "uint24"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], poolAddress);
                const result = await poolContract.methods.fee().call();
                return parseInt(result);
            },
            forceUpdate,
            DEFAULT_CACHE_DURATION,
            poolAddress
        );
    }

    /**
     * 获取缓存的Token Symbol
     */
    async getCachedTokenSymbol(tokenAddress, forceUpdate = false) {
        return this.executeWithCache(
            'tokenSymbol',
            async () => {
                const web3 = getWeb3();
                const tokenContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "symbol",
                        "outputs": [{"internalType": "string", "name": "", "type": "string"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], tokenAddress);
                return await tokenContract.methods.symbol().call();
            },
            forceUpdate,
            10 * 60 * 1000, // Token信息缓存10分钟
            tokenAddress
        );
    }

    /**
     * 获取缓存的Allowance
     */
    async getCachedAllowance(tokenAddress, sender, spender, forceUpdate = false) {
        return this.executeWithCache(
            'tokenAllowance',
            async () => {
                const web3 = getWeb3();
                const tokenContract = new web3.eth.Contract([
                    {
                        "inputs": [
                            {"internalType": "address", "name": "owner", "type": "address"},
                            {"internalType": "address", "name": "spender", "type": "address"}
                        ],
                        "name": "allowance",
                        "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], tokenAddress);
                const result = await tokenContract.methods.allowance(sender, spender).call();
                return BigInt(result);
            },
            forceUpdate,
            60 * 1000, // Allowance缓存30秒
            tokenAddress,
            sender,
            spender
        );
    }

    /**
     * 获取Token余额
     */
    async getTokenBalance(tokenAddress, accountAddress, forceUpdate = false) {
        return this.executeWithCache(
            'tokenBalance',
            async () => {
                const web3 = getWeb3();
                const tokenContract = new web3.eth.Contract([
                    {
                        "inputs": [{"internalType": "address", "name": "account", "type": "address"}],
                        "name": "balanceOf",
                        "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], tokenAddress);
                const result = await tokenContract.methods.balanceOf(accountAddress).call();
                return BigInt(result);
            },
            forceUpdate,
            60 * 1000, // 余额缓存15秒
            tokenAddress,
            accountAddress
        );
    }

    /**
     * 获取Gas Token余额 (ETH/BNB等)
     */
    async getGasTokenBalance(accountAddress, forceUpdate = false) {
        return this.executeWithCache(
            'gasTokenBalance',
            async () => {
                const web3 = getWeb3();
                const result = await web3.eth.getBalance(accountAddress);
                return BigInt(result);
            },
            forceUpdate,
            60 * 1000, // 余额缓存15秒
            accountAddress
        );
    }

    /**
     * 获取Token精度
     */
    async getTokenDecimals(tokenAddress, forceUpdate = false) {
        return this.executeWithCache(
            'tokenDecimals',
            async () => {
                const web3 = getWeb3();
                const tokenContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "decimals",
                        "outputs": [{"internalType": "uint8", "name": "", "type": "uint8"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], tokenAddress);
                const result = await tokenContract.methods.decimals().call();
                return parseInt(result);
            },
            forceUpdate,
            10 * 60 * 1000, // Token信息缓存10分钟
            tokenAddress
        );
    }

    /**
     * 获取Token名称
     */
    async getTokenName(tokenAddress, forceUpdate = false) {
        return this.executeWithCache(
            'tokenName',
            async () => {
                const web3 = getWeb3();
                const tokenContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "name",
                        "outputs": [{"internalType": "string", "name": "", "type": "string"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], tokenAddress);
                return await tokenContract.methods.name().call();
            },
            forceUpdate,
            10 * 60 * 1000, // Token信息缓存10分钟
            tokenAddress
        );
    }

    /**
     * 获取Pool Slot0信息
     */
    async getPoolSlot0(poolAddress, forceUpdate = false) {
        return this.executeWithCache(
            'poolSlot0',
            async () => {
                const web3 = getWeb3();
                const poolContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "slot0",
                        "outputs": [
                            {"internalType": "uint160", "name": "sqrtPriceX96", "type": "uint160"},
                            {"internalType": "int24", "name": "tick", "type": "int24"},
                            {"internalType": "uint16", "name": "observationIndex", "type": "uint16"},
                            {"internalType": "uint16", "name": "observationCardinality", "type": "uint16"},
                            {"internalType": "uint16", "name": "observationCardinalityNext", "type": "uint16"},
                            {"internalType": "uint8", "name": "feeProtocol", "type": "uint8"},
                            {"internalType": "bool", "name": "unlocked", "type": "bool"}
                        ],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], poolAddress);
                const result = await poolContract.methods.slot0().call();
                return {
                    sqrtPriceX96: BigInt(result.sqrtPriceX96),
                    tick: parseInt(result.tick),
                    observationIndex: parseInt(result.observationIndex),
                    observationCardinality: parseInt(result.observationCardinality),
                    observationCardinalityNext: parseInt(result.observationCardinalityNext),
                    feeProtocol: parseInt(result.feeProtocol),
                    unlocked: result.unlocked
                };
            },
            forceUpdate,
            10 * 1000, // Slot0信息缓存5秒
            poolAddress
        );
    }

    /**
     * 获取Pool Tick Spacing
     */
    async getPoolTickSpacing(poolAddress, forceUpdate = false) {
        return this.executeWithCache(
            'poolTickSpacing',
            async () => {
                const web3 = getWeb3();
                const poolContract = new web3.eth.Contract([
                    {
                        "inputs": [],
                        "name": "tickSpacing",
                        "outputs": [{"internalType": "int24", "name": "", "type": "int24"}],
                        "stateMutability": "view",
                        "type": "function"
                    }
                ], poolAddress);
                const result = await poolContract.methods.tickSpacing().call();
                return parseInt(result);
            },
            forceUpdate,
            DEFAULT_CACHE_DURATION,
            poolAddress
        );
    }

    /**
     * 清除所有缓存
     */
    clearAllCache() {
        memoryCache = {};
        localStorage.removeItem(CACHE_STORAGE_KEY);
    }

    /**
     * 清除特定键的缓存
     */
    clearCache(functionName, ...params) {
        const cacheKey = this.generateCacheKey(functionName, ...params);
        delete memoryCache[cacheKey];
        this.saveCacheToStorage();
    }

    /**
     * 获取缓存统计信息
     */
    getCacheStats() {
        const keys = Object.keys(memoryCache);
        const now = Date.now();
        let validCount = 0;
        let expiredCount = 0;

        keys.forEach(key => {
            const data = memoryCache[key];
            if (data && (now - data.timestamp) < DEFAULT_CACHE_DURATION) {
                validCount++;
            } else {
                expiredCount++;
            }
        });

        return {
            totalEntries: keys.length,
            validEntries: validCount,
            expiredEntries: expiredCount
        };
    }
}

// 创建单例实例
const cachedWeb3Utils = new CachedWeb3Utils();

export default cachedWeb3Utils; 